// App-proxy-only storefront intake. Never submits a native Shopify contact form.
const ENDPOINT = '/apps/123homeoffice-b2b/inquiry';
const MAX_LINES = 10;
const ID = /^[1-9]\d*$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STANDARD_DISCOUNTS = [[1, 0], [2, 3], [5, 5], [10, 8], [20, 10]];

export function standardPreview(quantity, shopPriceCents) {
  if (!Number.isInteger(quantity) || quantity < 1 || quantity > 10000 ||
      !Number.isSafeInteger(shopPriceCents) || shopPriceCents < 0) return null;
  const discountPercent = [...STANDARD_DISCOUNTS].reverse().find(([minimum]) => quantity >= minimum)[1];
  const unitGrossCents = Math.round(shopPriceCents * (100 - discountPercent) / 100);
  if (!Number.isSafeInteger(unitGrossCents) || !Number.isSafeInteger(unitGrossCents * quantity) ||
      !Number.isSafeInteger(shopPriceCents * quantity)) return null;
  return { discountPercent, unitGrossCents, totalGrossCents: unitGrossCents * quantity,
    savingGrossCents: (shopPriceCents - unitGrossCents) * quantity };
}

export function combinedPreview(lines) {
  if (!Array.isArray(lines) || lines.length < 1 || lines.length > MAX_LINES) return null;
  let shopGrossCents = 0, totalGrossCents = 0, savingGrossCents = 0;
  for (const line of lines) {
    const result = standardPreview(line.quantity, line.shopPriceCents);
    if (!result) return null;
    const shopLine = line.quantity * line.shopPriceCents;
    shopGrossCents += shopLine;
    totalGrossCents += result.totalGrossCents;
    savingGrossCents += result.savingGrossCents;
    if (![shopGrossCents, totalGrossCents, savingGrossCents].every(Number.isSafeInteger)) return null;
  }
  return { shopGrossCents, totalGrossCents, savingGrossCents };
}

export function parsePreviewTaxRate(raw) {
  const text = String(raw ?? '').trim();
  if (!/^\d{1,3}(?:[.,]\d{1,2})?$/.test(text)) return null;
  const [whole, decimal = ''] = text.replace(',', '.').split('.');
  const basisPoints = Number(whole) * 100 + Number(decimal.padEnd(2, '0'));
  return basisPoints <= 10000 ? basisPoints : null;
}

export function taxPreview(grossCents, rateBasisPoints) {
  if (!Number.isSafeInteger(grossCents) || grossCents < 0 ||
      !Number.isInteger(rateBasisPoints) || rateBasisPoints < 0 || rateBasisPoints > 10000) return null;
  const denominator = 10000n + BigInt(rateBasisPoints);
  const netCents = Number((BigInt(grossCents) * 10000n + denominator / 2n) / denominator);
  if (!Number.isSafeInteger(netCents)) return null;
  return { netCents, taxCents: grossCents - netCents, grossCents };
}

export function combinedTaxPreview(grossLines, rateBasisPoints) {
  if (!Array.isArray(grossLines) || grossLines.length < 1 || grossLines.length > MAX_LINES) return null;
  let netCents = 0, taxCents = 0, grossCents = 0;
  for (const grossLine of grossLines) {
    const part = taxPreview(grossLine, rateBasisPoints);
    if (!part) return null;
    netCents += part.netCents; taxCents += part.taxCents; grossCents += part.grossCents;
    if (![netCents, taxCents, grossCents].every(Number.isSafeInteger)) return null;
  }
  return { netCents, taxCents, grossCents };
}

export function netPreviewSaving(shopGrossCents, discountedGrossCents, rateBasisPoints) {
  if (!Number.isSafeInteger(shopGrossCents) || !Number.isSafeInteger(discountedGrossCents) ||
      discountedGrossCents < 0 || discountedGrossCents > shopGrossCents) return null;
  const shop = taxPreview(shopGrossCents, rateBasisPoints);
  const discounted = taxPreview(discountedGrossCents, rateBasisPoints);
  return shop && discounted ? shop.netCents - discounted.netCents : null;
}

function safeImageURL(raw, origin) {
  if (typeof raw !== 'string' || raw.length > 2048) return null;
  try {
    const url = new URL(raw, origin);
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

export function productHandle(value, origin) {
  const raw = String(value || '').trim();
  if (/^[a-z0-9][a-z0-9-]*$/.test(raw)) return raw;
  try {
    const url = new URL(raw);
    if (url.origin !== origin || url.username || url.password) return null;
    return /^\/products\/([a-z0-9][a-z0-9-]*)\/?$/.exec(url.pathname)?.[1] || null;
  } catch { return null; }
}

export function checkedProduct(value) {
  if (!value || typeof value !== 'object' || typeof value.title !== 'string' ||
      !value.title.trim() || !/^[a-z0-9][a-z0-9-]*$/.test(String(value.handle)) ||
      !Array.isArray(value.variants) || value.variants.length < 1 || value.variants.length > 1000) return null;
  const variants = value.variants.map(v => ({
    id: String(v?.id ?? ''), title: String(v?.title ?? ''), available: v?.available === true,
    priceCents: Number.isSafeInteger(v?.price) && v.price >= 0 ? v.price : null,
    image: typeof v?.featured_image?.src === 'string' ? v.featured_image.src : null,
  }));
  if (variants.some(v => !ID.test(v.id) || !v.title.trim()) ||
      new Set(variants.map(v => v.id)).size !== variants.length) return null;
  return { title: value.title.trim().slice(0, 500), handle: value.handle, variants,
    image: typeof value.featured_image === 'string' ? value.featured_image : null };
}

async function limitedJSON(response, maxBytes, allowShopifyJavascript = false) {
  if (response.status !== 200) throw Error('HTTP_' + response.status);
  if (response.redirected) throw Error('REDIRECT');
  const contentType = response.headers.get('content-type') || '';
  if (!/^application\/json(?:\s*;|$)/i.test(contentType) &&
      !(allowShopifyJavascript && /^text\/javascript(?:\s*;|$)/i.test(contentType))) throw Error('CONTENT_TYPE');
  const reader = response.body?.getReader();
  if (!reader) throw Error('NO_BODY');
  let size = 0, output = '';
  const decoder = new TextDecoder();
  try {
    for (;;) {
      const part = await reader.read();
      if (part.done) break;
      size += part.value.byteLength;
      if (size > maxBytes) { await reader.cancel(); throw Error('BODY_TOO_LARGE'); }
      output += decoder.decode(part.value, { stream: true });
    }
    output += decoder.decode();
  } finally { reader.releaseLock(); }
  return JSON.parse(output);
}

export async function loadProduct(handle, fetcher = fetch) {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), 10000);
  try {
    const response = await fetcher('/products/' + encodeURIComponent(handle) + '.js', {
      method: 'GET', credentials: 'same-origin', mode: 'same-origin', redirect: 'error',
      cache: 'no-store', headers: { Accept: 'application/json' }, signal: abort.signal,
    });
    const product = checkedProduct(await limitedJSON(response, 524288, true));
    if (!product || product.handle !== handle) throw Error('INVALID_PRODUCT');
    return product;
  } finally { clearTimeout(timer); }
}

export async function suggestProducts(query, fetcher = fetch, routeRoot = '/', signal) {
  const term = String(query || '').trim();
  if (term.length < 2 || term.length > 80) return [];
  const root = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(routeRoot) ? routeRoot : '/';
  const params = new URLSearchParams({ q: term, 'resources[type]': 'product',
    'resources[limit]': '6', 'resources[options][unavailable_products]': 'hide' });
  const response = await fetcher(root + 'search/suggest.json?' + params, {
    method: 'GET', credentials: 'same-origin', mode: 'same-origin', redirect: 'error',
    cache: 'no-store', headers: { Accept: 'application/json' }, signal,
  });
  const data = await limitedJSON(response, 262144);
  const products = data?.resources?.results?.products;
  if (!Array.isArray(products) || products.length > 6) throw Error('INVALID_SUGGESTIONS');
  return products.filter(p => p && typeof p.title === 'string' && p.title.trim() &&
    typeof p.handle === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(p.handle))
    .map(p => ({ title: p.title.trim().slice(0, 160), handle: p.handle }));
}

export async function sendInquiry(payload, fetcher = fetch) {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), 10000);
  try {
    const response = await fetcher(ENDPOINT, {
      method: 'POST', credentials: 'same-origin', mode: 'same-origin', redirect: 'error',
      cache: 'no-store', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload), signal: abort.signal,
    });
    const result = await limitedJSON(response, 8192);
    if (!result || typeof result !== 'object' || Array.isArray(result) ||
        Object.keys(result).some(k => !['ok', 'inquiryNumber'].includes(k)) ||
        result.ok !== true || typeof result.inquiryNumber !== 'string' ||
        !/^INQ-[A-Za-z0-9-]{1,100}$/.test(result.inquiryNumber)) throw Error('UNCONFIRMED');
    return result.inquiryNumber;
  } finally { clearTimeout(timer); }
}

export function buildInput(state, lines, createId) {
  if (!state || !['PRODUCT', 'GENERIC'].includes(state.sourceMode) ||
      typeof state.contactName !== 'string' || !state.contactName.trim() || state.contactName.length > 160 ||
      typeof state.email !== 'string' || state.email.length > 254 || !EMAIL.test(state.email.trim()) ||
      typeof state.website !== 'string' || state.website.trim() ||
      !/^[A-Za-z]{2}$/.test(state.customerCountryCode || '') ||
      (!state.sameCountry && !/^[A-Za-z]{2}$/.test(state.deliveryCountryCode || '')) ||
      !Array.isArray(lines) || lines.length > MAX_LINES ||
      (state.sourceMode === 'PRODUCT' && lines.length < 1) ||
      (state.sourceMode === 'GENERIC' && lines.length !== 0)) return null;
  const ids = lines.map(x => x.shopifyVariantId);
  if (new Set(ids).size !== ids.length || lines.some(x =>
    !x.productTitle || x.productTitle.length > 500 || !ID.test(x.shopifyVariantId) ||
    !Number.isInteger(x.quantity) || x.quantity < 1 || x.quantity > 10000)) return null;
  const clientSubmissionId = createId();
  if (typeof clientSubmissionId !== 'string' || !/^[A-Za-z0-9_-]{8,80}$/.test(clientSubmissionId)) return null;
  return {
    clientSubmissionId, sourceType: state.sourceMode, website: '',
    companyName: state.companyName?.trim() || null,
    contactName: state.contactName.trim(), email: state.email.trim().toLowerCase(),
    phone: state.phone?.trim() || null, message: state.message?.trim() || null,
    requestedProductText: state.sourceMode === 'GENERIC' ? state.requestedProductText?.trim() || null : null,
    customerCountryCode: state.customerCountryCode.toUpperCase(),
    deliveryCountryCode: (state.sameCountry ? state.customerCountryCode : state.deliveryCountryCode).toUpperCase(),
    sameCountry: state.sameCountry, lines,
  };
}

export function mount(doc = document, win = window) {
  const roots = [...doc.querySelectorAll('[data-r01-m1-inquiry]')];
  if (roots.length !== 1) return;
  const root = roots[0], fieldset = root.querySelector('fieldset');
  const status = root.querySelector('[data-status]');
  const productArea = root.querySelector('[data-product-area]');
  const list = root.querySelector('[data-product-lines]'), add = root.querySelector('[data-add-product]');
  const nav = root.querySelector('[data-product-navigation]'), tabs = root.querySelector('[data-product-tabs]');
  const activeImage = root.querySelector('[data-active-product-image]');
  const combinedLines = root.querySelector('[data-combined-lines]');
  const combinedShop = root.querySelector('[data-combined-shop]');
  const combinedSaving = root.querySelector('[data-combined-saving]');
  const combinedNet = root.querySelector('[data-combined-net]');
  const combinedTaxLabel = root.querySelector('[data-combined-tax-label]');
  const combinedTax = root.querySelector('[data-combined-tax]');
  const combinedTotal = root.querySelector('[data-combined-total]');
  const combinedNetSaving = root.querySelector('[data-combined-net-saving]');
  const previewTaxChoice = root.querySelector('[data-preview-tax-choice]');
  const previewTaxCustom = root.querySelector('[data-preview-tax-custom]');
  const previewTaxCustomField = root.querySelector('[data-preview-tax-custom-field]');
  const previewTaxStatus = root.querySelector('[data-preview-tax-status]');
  const formSummary = root.querySelector('[data-product-form-summary]');
  const send = root.querySelector('[data-send]');
  const feedback = root.querySelector('[data-submit-feedback]');
  const sameCountry = root.querySelector('[name="sameCountry"]');
  const firmCountry = root.querySelector('[name="customerCountryCode"]');
  const deliveryCountry = root.querySelector('[name="deliveryCountryCode"]');
  if (root.dataset.bound || root.dataset.enabled !== 'true' || !fieldset || !status ||
      !productArea || !list || !add || !nav || !tabs || !activeImage ||
      !combinedLines || !combinedShop || !combinedSaving || !combinedNet || !combinedTaxLabel || !combinedTax ||
      !combinedTotal || !combinedNetSaving || !previewTaxChoice || !previewTaxCustom || !previewTaxCustomField ||
      !previewTaxStatus || !formSummary || !send || !feedback || !sameCountry || !firmCountry || !deliveryCountry ||
      root.closest('form') || root.querySelector('form') ||
      typeof win.crypto?.randomUUID !== 'function' || typeof win.fetch !== 'function') return;
  root.dataset.bound = 'true';
  let attempted = false;
  const slots = [];
  let activeSlot = null, slotSequence = 0;
  const say = message => { status.textContent = message; };
  const showFeedback = (message, result, moveFocus = false) => {
    feedback.textContent = message;
    feedback.dataset.result = result;
    feedback.hidden = false;
    if (moveFocus) {
      feedback.scrollIntoView?.({ block: 'center' });
      feedback.focus?.({ preventScroll: true });
    }
  };
  const field = name => root.querySelector('[name="' + name + '"]');
  const money = cents => new Intl.NumberFormat('de-AT', { style: 'currency',
    currency: /^[A-Z]{3}$/.test(win.Shopify?.currency?.active || '') ? win.Shopify.currency.active : 'EUR',
  }).format(cents / 100);
  const previewRate = () => parsePreviewTaxRate(previewTaxChoice.value === 'custom'
    ? previewTaxCustom.value : previewTaxChoice.value);
  const previewRateText = () => previewRate() === null ? null
    : (previewRate() / 100).toLocaleString('de-AT', { maximumFractionDigits: 2 });

  function chosenVariant(slot) {
    return slot.product?.variants.find(v => v.id === slot.select.value && v.available) || null;
  }

  function activateSlot(slot, focus = false) {
    if (!slots.includes(slot)) return;
    activeSlot = slot;
    for (const current of slots) {
      const selected = current === slot;
      current.row.hidden = !selected;
      current.tab.setAttribute('aria-selected', String(selected));
      current.tab.tabIndex = selected ? 0 : -1;
      current.navButton.classList.toggle('is-active', selected);
      current.navButton.setAttribute('aria-current', selected ? 'true' : 'false');
    }
    const variant = chosenVariant(slot);
    const imageURL = safeImageURL(variant?.image || slot.product?.image, win.location.origin);
    if (imageURL) { activeImage.src = imageURL; activeImage.alt = slot.product.title; activeImage.hidden = false; }
    else { activeImage.removeAttribute('src'); activeImage.alt = ''; activeImage.hidden = true; }
    if (focus) slot.tab.focus();
  }

  function refreshOverview() {
    for (const slot of slots) {
      const variant = chosenVariant(slot);
      const name = slot.product?.title || 'Produkt laden';
      slot.tab.textContent = name.length > 22 ? name.slice(0, 21) + '…' : name;
      slot.tab.setAttribute('aria-label', 'Produkt ' + (slots.indexOf(slot) + 1) + ': ' + name);
      slot.navText.textContent = name;
      slot.navQty.textContent = slot.quantity.value + ' Stk.';
      slot.navRow.hidden = !slot.product;
      slot.navRemove.setAttribute('aria-label', name + ' aus der Kalkulation entfernen');
      const imageURL = safeImageURL(variant?.image || slot.product?.image, win.location.origin);
      if (imageURL) { slot.navImage.src = imageURL; slot.navImage.hidden = false; }
      else { slot.navImage.removeAttribute('src'); slot.navImage.hidden = true; }
      slot.navRemove.disabled = attempted;
    }
    if (activeSlot) activateSlot(activeSlot);
    const detail = slots.map(slot => {
      const variant = chosenVariant(slot);
      const quantity = Number(slot.quantity.value);
      return { slot, variant, quantity, result: standardPreview(quantity, variant?.priceCents) };
    });
    combinedLines.replaceChildren(); formSummary.replaceChildren();
    const heading = doc.createElement('h4'); heading.textContent = 'Ihre Produktauswahl'; formSummary.append(heading);
    const summaryList = doc.createElement('ul'); summaryList.className = 'r01-m1-form-product-list'; formSummary.append(summaryList);
    for (const { slot, variant, quantity, result } of detail) {
      const label = slot.product?.title || 'Produkt noch nicht geladen';
      const suffix = variant && variant.title !== 'Default Title' ? ' · ' + variant.title : '';
      const imageURL = safeImageURL(variant?.image || slot.product?.image, win.location.origin);
      const line = doc.createElement('div'); line.className = 'r01-m1-line-detail';
      const lineIdentity = doc.createElement('span'); lineIdentity.className = 'r01-m1-line-identity';
      if (imageURL) {
        const image = doc.createElement('img'); image.src = imageURL; image.alt = ''; image.loading = 'lazy'; lineIdentity.append(image);
      } else {
        const placeholder = doc.createElement('span'); placeholder.className = 'r01-m1-form-product-placeholder';
        placeholder.setAttribute('aria-hidden', 'true'); lineIdentity.append(placeholder);
      }
      const lineName = doc.createElement('span'); lineName.textContent = label + suffix + ' · ' + quantity + ' Stück';
      const linePrice = doc.createElement('strong'); linePrice.textContent = result ? money(result.totalGrossCents) : '–';
      lineIdentity.append(lineName); line.append(lineIdentity, linePrice); combinedLines.append(line);
      if (!slot.product || !variant) continue;
      const listItem = doc.createElement('li');
      if (imageURL) {
        const image = doc.createElement('img'); image.src = imageURL; image.alt = ''; image.loading = 'lazy'; listItem.append(image);
      } else {
        const placeholder = doc.createElement('span'); placeholder.className = 'r01-m1-form-product-placeholder';
        placeholder.setAttribute('aria-hidden', 'true'); listItem.append(placeholder);
      }
      const description = doc.createElement('span'); description.className = 'r01-m1-form-product-description';
      const productName = doc.createElement('strong'); productName.textContent = label;
      const variantName = doc.createElement('small'); variantName.textContent = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
      description.append(productName, variantName);
      const count = doc.createElement('span'); count.className = 'r01-m1-form-product-count'; count.textContent = quantity + ' Stk.';
      listItem.append(description, count); summaryList.append(listItem);
    }
    formSummary.hidden = summaryList.childElementCount === 0;
    const all = detail.every(item => item.variant && item.result);
    const total = all ? combinedPreview(detail.map(item => ({ quantity: item.quantity, shopPriceCents: item.variant.priceCents }))) : null;
    combinedShop.textContent = total ? money(total.shopGrossCents) : '–';
    combinedSaving.textContent = total ? '−' + money(total.savingGrossCents) : '–';
    const tax = total ? combinedTaxPreview(detail.map(item => item.result.totalGrossCents), previewRate()) : null;
    const shopTax = total ? combinedTaxPreview(detail.map(item => item.quantity * item.variant.priceCents), previewRate()) : null;
    combinedTaxLabel.textContent = 'Umsatzsteuer' + (previewRateText() === null ? ' (Vorschau)' : ' (' + previewRateText() + ' % Vorschau)');
    combinedNet.textContent = tax ? money(tax.netCents) : previewRate() === null ? 'USt-Satz prüfen' : '–';
    combinedTax.textContent = tax ? money(tax.taxCents) : previewRate() === null ? 'USt-Satz prüfen' : '–';
    combinedTotal.textContent = total ? money(total.totalGrossCents) : 'Noch nicht vollständig';
    combinedNetSaving.textContent = tax && shopTax ? money(shopTax.netCents - tax.netCents)
      : previewRate() === null ? 'USt-Satz prüfen' : '–';
  }

  function addSlot(initial = '', preferred = '') {
    if (slots.length >= MAX_LINES) return;
    const sequence = ++slotSequence;
    const row = doc.createElement('div'); row.dataset.productLine = 'true'; row.id = 'r01-product-panel-' + sequence;
    row.setAttribute('role', 'tabpanel'); row.setAttribute('aria-labelledby', 'r01-product-tab-' + sequence);
    const tab = doc.createElement('button'); tab.type = 'button'; tab.id = 'r01-product-tab-' + sequence;
    tab.setAttribute('role', 'tab'); tab.setAttribute('aria-controls', row.id);
    const navRow = doc.createElement('div'); navRow.className = 'r01-m1-nav-row';
    const navButton = doc.createElement('button'); navButton.type = 'button'; navButton.className = 'r01-m1-nav-select';
    const navImage = doc.createElement('img'); navImage.alt = ''; navImage.hidden = true;
    const navText = doc.createElement('span'); const navQty = doc.createElement('span'); navQty.className = 'r01-m1-nav-qty';
    navButton.append(navImage, navText, navQty);
    const navRemove = doc.createElement('button'); navRemove.type = 'button'; navRemove.className = 'r01-m1-nav-remove';
    navRemove.textContent = '×'; navRow.append(navButton, navRemove);
    const controls = doc.createElement('div'); controls.className = 'r01-m1-product-controls';
    const searchWrap = doc.createElement('div'); searchWrap.className = 'r01-m1-search-wrap';
    const label = doc.createElement('label'); label.textContent = 'Produkt suchen oder Produktlink eingeben';
    const input = doc.createElement('input'); input.type = 'search'; input.maxLength = 1000; input.value = initial;
    input.setAttribute('role', 'combobox'); input.setAttribute('aria-autocomplete', 'list');
    const results = doc.createElement('div'); results.className = 'r01-m1-search-results';
    results.id = 'r01-product-suggestions-' + sequence; results.setAttribute('role', 'listbox'); results.hidden = true;
    input.setAttribute('aria-controls', results.id); input.setAttribute('aria-expanded', 'false');
    label.append(input); searchWrap.append(label, results); controls.append(searchWrap);
    const load = doc.createElement('button'); load.type = 'button'; load.dataset.loadProduct = 'true'; load.textContent = 'Produkt laden'; controls.append(load);
    const variantLabel = doc.createElement('label'); variantLabel.className = 'r01-m1-variant-field'; variantLabel.textContent = 'Variante';
    const select = doc.createElement('select'); select.disabled = true; variantLabel.append(select); controls.append(variantLabel);
    const quantityLabel = doc.createElement('label'); quantityLabel.className = 'r01-m1-quantity-field'; quantityLabel.textContent = 'Menge';
    const quantity = doc.createElement('input'); quantity.type = 'number'; quantity.min = '1'; quantity.max = '10000'; quantity.value = '1';
    quantityLabel.append(quantity); controls.append(quantityLabel);
    const summary = doc.createElement('div'); summary.className = 'r01-m1-product-summary'; summary.hidden = true;
    const title = doc.createElement('strong');
    const shopPrice = doc.createElement('p'); shopPrice.className = 'r01-m1-shop-price';
    const currentDiscount = doc.createElement('p'); currentDiscount.className = 'r01-m1-current-discount';
    const preview = doc.createElement('p'); preview.setAttribute('aria-live', 'polite');
    const tiers = doc.createElement('div'); tiers.className = 'r01-m1-discount-tiers'; tiers.setAttribute('aria-label', 'Standardrabattstaffel');
    const breakdown = doc.createElement('div'); breakdown.className = 'r01-m1-item-breakdown';
    const breakdownValue = (labelText, extraClass = '') => {
      const line = doc.createElement('div'); line.className = 'r01-m1-price-row' + extraClass;
      const label = doc.createElement('span'); label.textContent = labelText;
      const value = doc.createElement('strong'); value.textContent = '–';
      line.append(label, value); breakdown.append(line); return value;
    };
    const itemShop = breakdownValue('Shoppreis / Stück brutto');
    const itemDiscount = breakdownValue('Ihr Mengenrabatt');
    const itemUnitGross = breakdownValue('Firmenpreis / Stück brutto', ' r01-m1-price-row--highlight');
    const itemNet = breakdownValue('Gesamtpreis netto (Vorschau)');
    const itemTax = breakdownValue('Umsatzsteuer (Vorschau)');
    const itemTotal = breakdownValue('Gesamtpreis brutto', ' r01-m1-price-row--total');
    const itemSaving = breakdownValue('Sie sparen gesamt brutto');
    summary.append(title, shopPrice, currentDiscount, tiers, breakdown, preview);
    row.append(controls, summary);
    const slot = { row, tab, navRow, navButton, navImage, navText, navQty, navRemove, input, select, quantity, product: null };
    slots.push(slot); list.append(row); tabs.append(tab); nav.append(navRow);
    add.disabled = slots.length >= MAX_LINES;
    navButton.addEventListener('click', () => activateSlot(slot));
    tab.addEventListener('click', () => activateSlot(slot));
    tab.addEventListener('keydown', event => {
      const index = slots.indexOf(slot);
      const next = event.key === 'ArrowRight' ? (index + 1) % slots.length
        : event.key === 'ArrowLeft' ? (index + slots.length - 1) % slots.length
        : event.key === 'Home' ? 0 : event.key === 'End' ? slots.length - 1 : -1;
      if (next < 0) return;
      event.preventDefault(); activateSlot(slots[next], true);
    });
    function renderPreview() {
      const variant = slot.product?.variants.find(v => v.id === select.value && v.available);
      summary.hidden = !variant;
      row.classList.toggle('is-loaded', !!variant);
      if (!variant) { refreshOverview(); return; }
      title.textContent = slot.product.title + (variant.title === 'Default Title' ? '' : ' · ' + variant.title);
      const result = standardPreview(Number(quantity.value), variant.priceCents);
      shopPrice.textContent = result ? 'Aktueller Shoppreis: ' + money(variant.priceCents) + ' brutto/Stück' : '';
      currentDiscount.textContent = result ? 'Aktueller Mengenrabatt: ' + result.discountPercent + ' %' : '';
      itemShop.textContent = result ? money(variant.priceCents) : '–';
      itemDiscount.textContent = result ? result.discountPercent + ' %' : '–';
      itemUnitGross.textContent = result ? money(result.unitGrossCents) : '–';
      const tax = result ? taxPreview(result.totalGrossCents, previewRate()) : null;
      const savingNetCents = result ? netPreviewSaving(variant.priceCents * Number(quantity.value),
        result.totalGrossCents, previewRate()) : null;
      itemTax.previousElementSibling.textContent = 'Umsatzsteuer' + (previewRateText() === null ? ' (Vorschau)' : ' (' + previewRateText() + ' % Vorschau)');
      itemNet.textContent = tax ? money(tax.netCents) : previewRate() === null ? 'USt-Satz prüfen' : '–';
      itemTax.textContent = tax ? money(tax.taxCents) : previewRate() === null ? 'USt-Satz prüfen' : '–';
      itemTotal.textContent = result ? money(result.totalGrossCents) : '–';
      itemSaving.textContent = result ? '−' + money(result.savingGrossCents) : '–';
      tiers.replaceChildren();
      const fill = doc.createElement('div'); fill.className = 'r01-m1-tier-fill';
      fill.style.width = result ? Math.min(100, STANDARD_DISCOUNTS.filter(([minimum]) => Number(quantity.value) >= minimum).length - 1) * 25 + '%' : '0%';
      tiers.append(fill);
      STANDARD_DISCOUNTS.forEach(([minimum, percent], index) => {
        const tier = doc.createElement('button'); tier.type = 'button';
        tier.className = 'r01-m1-tier-marker' + (result?.discountPercent === percent ? ' is-active' : '');
        tier.style.left = index * 25 + '%';
        tier.setAttribute('aria-label', 'Ab ' + minimum + ' Stück, ' + percent + ' Prozent Standardrabatt');
        tier.setAttribute('aria-pressed', String(result?.discountPercent === percent));
        const dot = doc.createElement('span'); dot.className = 'r01-m1-tier-dot'; dot.setAttribute('aria-hidden', 'true');
        const caption = doc.createElement('span'); caption.className = 'r01-m1-tier-caption';
        caption.textContent = (minimum === 20 ? '20+' : String(minimum)) + ' · ' + percent + ' %';
        tier.append(dot, caption);
        tier.addEventListener('click', () => { quantity.value = String(minimum); renderPreview(); });
        tiers.append(tier);
      });
      preview.textContent = result
        ? 'Ihr Standardrabatt ' + result.discountPercent + ' % · Vorschau ' +
          (savingNetCents === null ? 'Nettoersparnis: USt-Satz prüfen' : money(savingNetCents) + ' Nettoersparnis gesamt')
        : 'Für diese Variante ist derzeit keine verlässliche Preisvorschau verfügbar.';
      refreshOverview();
    }
    slot.renderPreview = renderPreview;
    let searchTimer, searchAbort, searchSequence = 0, pressedSuggestion = null;
    const closeSuggestions = () => { results.replaceChildren(); results.hidden = true; input.setAttribute('aria-expanded', 'false'); };
    const cancelSuggestions = () => { clearTimeout(searchTimer); searchAbort?.abort(); searchSequence += 1; closeSuggestions(); };
    const chooseSuggestion = option => {
      if (!results.contains(option)) return;
      const handle = option.dataset.handle;
      cancelSuggestions(); input.value = handle; load.click();
    };
    input.addEventListener('input', () => {
      searchWrap.classList.remove('is-new-product');
      slot.product = null; select.replaceChildren(); select.disabled = true; summary.hidden = true;
      row.classList.remove('is-loaded'); refreshOverview(); cancelSuggestions();
      const query = input.value.trim();
      if (query.length < 2 || query.length > 80 || query.includes('/')) return;
      const sequence = searchSequence;
      searchTimer = setTimeout(async () => {
        const abort = new AbortController(); searchAbort = abort;
        const timeout = setTimeout(() => abort.abort(), 8000);
        try {
          const products = await suggestProducts(query, win.fetch.bind(win), win.Shopify?.routes?.root || '/', abort.signal);
          if (sequence !== searchSequence || !row.isConnected || input.value.trim() !== query) return;
          closeSuggestions();
          for (const product of products) {
            const option = doc.createElement('button'); option.type = 'button'; option.setAttribute('role', 'option');
            option.textContent = product.title; option.dataset.handle = product.handle;
            option.addEventListener('click', () => chooseSuggestion(option));
            results.append(option);
          }
          results.hidden = products.length === 0; input.setAttribute('aria-expanded', String(products.length > 0));
        } catch (error) {
          if (!abort.signal.aborted && sequence === searchSequence) { closeSuggestions(); say('Produktsuche derzeit nicht verfügbar. Ein Produktlink kann weiterhin geladen werden.'); }
        } finally { clearTimeout(timeout); if (searchAbort === abort) searchAbort = null; }
      }, 250);
    });
    input.addEventListener('keydown', event => {
      if (event.key === 'Escape') { cancelSuggestions(); return; }
      if (event.key === 'ArrowDown' && !results.hidden) { event.preventDefault(); results.querySelector('button')?.focus(); }
    });
    results.addEventListener('pointerdown', event => {
      const option = event.target.closest?.('button[role="option"]');
      pressedSuggestion = option && results.contains(option)
        ? { option, x: event.clientX, y: event.clientY } : null;
    });
    results.addEventListener('pointerup', event => {
      const pressed = pressedSuggestion; pressedSuggestion = null;
      if (!pressed || pressed.option !== event.target.closest?.('button[role="option"]')) return;
      if (Math.hypot(event.clientX - pressed.x, event.clientY - pressed.y) <= 10) chooseSuggestion(pressed.option);
    });
    results.addEventListener('pointercancel', () => { pressedSuggestion = null; });
    results.addEventListener('keydown', event => {
      const options = [...results.querySelectorAll('button')]; const index = options.indexOf(doc.activeElement);
      if (event.key === 'Escape') { cancelSuggestions(); input.focus(); }
      else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault(); options[(index + (event.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length]?.focus();
      }
    });
    searchWrap.addEventListener('focusout', event => {
      if (!pressedSuggestion && !searchWrap.contains(event.relatedTarget)) closeSuggestions();
    });
    select.addEventListener('change', renderPreview);
    quantity.addEventListener('input', renderPreview);
    navRemove.addEventListener('click', () => {
      if (attempted) return;
      cancelSuggestions();
      if (slots.length === 1) {
        slot.product = null; input.value = ''; select.replaceChildren(); select.disabled = true;
        summary.hidden = true; row.classList.remove('is-loaded'); refreshOverview(); input.focus(); return;
      }
      const index = slots.indexOf(slot); row.remove(); tab.remove(); navRow.remove(); slots.splice(index, 1);
      add.disabled = false; activateSlot(slots[Math.min(index, slots.length - 1)]); refreshOverview();
    });
    load.addEventListener('click', async () => {
      if (attempted) return;
      cancelSuggestions();
      const handle = productHandle(input.value, win.location.origin);
      if (!handle) { say('Bitte einen Produktlink dieses Shops eingeben.'); return; }
      load.disabled = true; say('Produkt wird geladen …');
      try {
        const product = await loadProduct(handle, win.fetch.bind(win));
        if (!row.isConnected || productHandle(input.value, win.location.origin) !== handle) return;
        slot.product = product; select.replaceChildren();
        for (const variant of product.variants) {
          const option = doc.createElement('option'); option.value = variant.id;
          option.textContent = variant.title === 'Default Title' ? 'Standard' : variant.title;
          option.disabled = !variant.available; select.append(option);
        }
        const selected = product.variants.find(v => v.id === preferred && v.available) || product.variants.find(v => v.available);
        if (!selected) throw Error('NO_AVAILABLE_VARIANT');
        select.value = selected.id; select.disabled = false; renderPreview(); say('Produkt geladen: ' + product.title);
      } catch (error) {
        const code = typeof error?.message === 'string' && /^[A-Z0-9_]+$/.test(error.message) ? error.message : 'NETWORK_OR_PARSE';
        win.console?.warn?.('R01_PRODUCT_LOAD_' + code);
        slot.product = null; select.replaceChildren(); select.disabled = true; summary.hidden = true; row.classList.remove('is-loaded'); refreshOverview();
        say('Produkt konnte nicht eindeutig geladen werden. Bitte Link und Verfügbarkeit prüfen.');
      }
      finally { load.disabled = false; }
    });
    activateSlot(slot); refreshOverview();
    if (initial) load.click();
    return slot;
  }

  const updateTaxPreview = () => {
    previewTaxCustomField.hidden = previewTaxChoice.value !== 'custom';
    const rate = previewRate();
    previewTaxStatus.textContent = rate === null ? 'Bitte einen USt-Satz von 0 bis 100 % mit höchstens zwei Nachkommastellen eingeben.'
      : 'Rechenannahme für diese Vorschau: ' + (rate / 100).toLocaleString('de-AT', { maximumFractionDigits: 2 }) + ' %.';
    for (const slot of slots) slot.renderPreview?.();
    refreshOverview();
  };
  previewTaxChoice.addEventListener('change', updateTaxPreview);
  previewTaxCustom.addEventListener('input', updateTaxPreview);
  updateTaxPreview();
  const syncCountries = () => {
    deliveryCountry.disabled = sameCountry.checked;
    if (sameCountry.checked) deliveryCountry.value = firmCountry.value;
  };
  sameCountry.addEventListener('change', syncCountries);
  firmCountry.addEventListener('change', syncCountries);
  syncCountries();
  add.addEventListener('click', () => {
    const slot = addSlot();
    if (!slot) return;
    slot.input.closest('.r01-m1-search-wrap')?.classList.add('is-new-product');
    slot.input.focus();
    slot.input.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  });
  const params = new URLSearchParams(win.location.search);
  const initial = params.get('product') || '';
  say('Bitte Produkte laden und Angaben ergänzen.');
  fieldset.disabled = false;
  if (initial) addSlot(initial, params.get('variant') || '');
  else addSlot();
  send.addEventListener('click', async () => {
    if (attempted) return;
    if (!root.isConnected || root.dataset.enabled !== 'true' || doc.querySelectorAll('[data-r01-m1-inquiry]').length !== 1) {
      fieldset.disabled = true; say('Anfrage gesperrt: Einbindung ist nicht eindeutig.'); return;
    }
    const lines = slots.map(slot => {
      const variant = slot.product?.variants.find(v => v.id === slot.select.value && v.available);
      const quantity = Number(slot.quantity.value);
      if (!variant || !Number.isInteger(quantity)) return null;
      return { productTitle: slot.product.title, productUrl: win.location.origin + '/products/' + slot.product.handle,
        variantTitle: variant.title === 'Default Title' ? null : variant.title,
        shopifyVariantId: variant.id, quantity };
    });
    if (lines.includes(null)) { say('Bitte jedes Produkt laden, eine verfügbare Variante und die Menge wählen.'); return; }
    const state = { sourceMode: 'PRODUCT', companyName: field('companyName').value,
      contactName: field('contactName').value, email: field('email').value,
      phone: field('phone').value, message: field('message').value,
      requestedProductText: null,
      customerCountryCode: field('customerCountryCode').value.trim(),
      deliveryCountryCode: field('deliveryCountryCode').value.trim(),
      sameCountry: field('sameCountry').checked, website: field('website').value };
    const payload = buildInput(state, lines, () => win.crypto.randomUUID());
    if (!payload) { say('Bitte Kontakt, Länder und Produktauswahl prüfen. Es wurde nichts gesendet.'); return; }
    attempted = true; fieldset.disabled = true; say('Anfrage wird übermittelt …');
    showFeedback('Anfrage wird übermittelt …', 'pending');
    try {
      const number = await sendInquiry(payload, win.fetch.bind(win));
      const message = 'Anfrage erfolgreich abgesendet. Ihre Anfragenummer: ' + number;
      say(message);
      showFeedback(message, 'success', true);
    } catch {
      const message = 'Kein bestätigter Eingang. Bitte nicht erneut senden oder neu laden; den Vorgang intern klären.';
      say(message);
      showFeedback(message, 'uncertain', true);
    }
  });
}

if (typeof document !== 'undefined') mount();
